import { markFetched, registerEntityBasedFetch } from "../common/hooks/initHook";
import { clientConfig } from "./clientConfigSlice";

const { createSlice, createSelector } = require("@reduxjs/toolkit");
const { ensureArray, groupBy } = require("../common/utils/arrayUtilities");
const { logout } = require("../common/reducers/initContext");
const { DefaultSuccessAction, DefaultFailureAction, initMapFactory, queryMapFactory, dataHookFactory } = require("../common/reducers/utilities");

const key = doc => doc.id;

const refDocSlice = createSlice({
    name: 'refDocs',
    initialState: {
        data: {}
    },
    reducers: {
        addOrUpdate: (state, action) => {
            const toUpdate = ensureArray(action.payload);
            const byDirectory = groupBy(toUpdate, 'directory');
            Object.entries(byDirectory).forEach(([ directory, files ]) => {
                const dir = directory === 'null' ? 0 : directory;
                if (state.data[dir]) {
                    const ids = new Set(files.map(d => key(d)));
                    state.data[dir] = (state.data[dir].filter(d => !ids.has(key(d))) ?? []).concat(files);
                } else {
                    state.data[dir] = files;
                }
            })
        },
        remove: (state, action) => {
            const toRemove = ensureArray(action.payload);
            const byDirectory = groupBy(toRemove, 'directory');
            Object.entries(byDirectory).forEach(([ directory, files ]) => {
                const dir = directory === 'null' ? 0 : directory;
                if (state.data[dir]) {
                    const ids = new Set(files.map(d => key(d)));
                    state.data[dir] = state.data[dir].filter(doc => !ids.has(key(doc)));
                }
            });
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => refDocSlice.getInitialState())
    }
})

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'referenceDoc_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'referenceDoc_one');

// const fetchAllDocs = network => network.get('/api/v1/refDocs', refDocSlice.actions.addOrUpdate, visualizeFailure('get'))
const fetchAllDocsInDirectory = directory => network => {
    return network.get(`/api/v1/refDocs/${directory ? encodeURIComponent(directory): ''}`, refDocSlice.actions.addOrUpdate, visualizeFailure('get'));
}
const fetchDocDetailsById = id => network => network.get(`/api/v1/refDocDetails/${encodeURIComponent(id)}`, refDocSlice.actions.addOrUpdate, visualizeFailure('get'));


export const removeRefDoc = (network, doc) => network.delete(`/api/v1/refDoc/${encodeURIComponent(doc.id)}`, visualizeSuccess('remove', refDocSlice.actions.remove), visualizeFailure('remove') )
export const updateRefDoc = (network, data) => network.put('/api/v1/refDoc', data, refDocSlice.actions.addOrUpdate, visualizeFailure('update'));
export const addRefDoc = (network, doc) => {
    let data = new FormData();
    data.append('file', doc.file);
    data.append('filename', doc.file.name);
    data.append('member_accessible', doc.member_accessible);
    data.append('type', 'file');
    doc.office?.forEach(o => {
        data.append('office', o);
    });
    if (doc.directory) {
        data.append('directory', doc.directory);
    }
    network.postData('/api/v1/refDoc', data);
}

export const addRefDocDir = (network, dir) => network.post('/api/v1/refDoc', { ...dir, type: 'directory' }, refDocSlice.actions.addOrUpdate, visualizeFailure('create'));
// #endregion

// #region selectors
const selectDocData = state => state.refDocs.data;
const selectAllDocs = createSelector([selectDocData], docs => Object.values(docs).flat());
const selectDocsInDirectory = directory => createSelector([selectDocData], docs => {
    return docs[directory ?? 0] ?? []
});
const selectDoc = id => createSelector([selectAllDocs], docs => docs.find(d => d.id === id));
// #endregion

// #region listeners
const listen = {
    refDocCreated: (doc, { dispatch, websocket }) => {
        markFetched(websocket, 'refDoc', key(doc));
        dispatch(refDocSlice.actions.addOrUpdate(doc))
    },
    refDocRemoved: (doc, { dispatch }) => dispatch(refDocSlice.actions.remove(doc))
}
// #endregion

const docInitByID = initMapFactory(fetchDocDetailsById, listen);
registerEntityBasedFetch('refDocs', docInitByID);

const docInitByDirectory = queryMapFactory(fetchAllDocsInDirectory, 'id', docInitByID, listen, { allowEmpty: true, name: "refDocsInDirectory"});

const cleanup = { root: refDocSlice.name, all: selectAllDocs, removeAll: refDocSlice.actions.remove, key };

export const [useDocsInDirectory] = dataHookFactory(selectDocsInDirectory, docInitByDirectory, { cleanup });
export const [useDoc] = dataHookFactory(selectDoc, docInitByID, { cleanup });

const fullRoles = ['administrator', 'document uploader'];

export const canRouteRefDocs = ({credentials}) => credentials.hasRole(['administrator', 'document uploader', 'member', 'auditor', 'member manager', 'member auditor', 'training manager', 'training auditor', 'event manager', 'template manager', 'event auditor', 'template auditor', 'rostering manager', 'rostering auditor', 'recurring rostering auditor', 'recurring rostering manager', 'equipment manager', 'equipment auditor', 'roster viewer'])
export const hasFullPermissions = (credentials) => checkPermissions(credentials, fullRoles);
export const canCreateRefDocs = hasFullPermissions;
export const canUpdateRefDocs = (credentials, doc) => checkPermissions(credentials, fullRoles, doc.office);
export const canRemoveRefDocs = canUpdateRefDocs;

const checkPermissions = (credentials, requiredRoles, offices) => {
    return credentials.hasRole(requiredRoles, {office: offices});
}

const reducer = { name: refDocSlice.name, reducer: refDocSlice.reducer }
export default reducer;
